/**
 * A 360° photo's cube as a KTX2 file (ADR-041): the media worker encodes it
 * with `ktx create` and checks it with `readKtx2Cube`, the headset reads it
 * with the same function and uploads the six faces as they are.
 *
 * Only what we make: one level, six faces, no supercompression, a square
 * face. For a format whose face size it knows (ASTC 6x6, RGBA8) the level
 * must be exactly six faces; for any other, six equal parts. Layout from the KTX 2.0 specification (little-endian throughout):
 * the 12-byte identifier, nine u32 (vkFormat, typeSize, pixelWidth,
 * pixelHeight, pixelDepth, layerCount, faceCount, levelCount,
 * supercompressionScheme), the index (u32 dfd offset and length, u32 kvd
 * offset and length, u64 sgd offset and length), then per level u64
 * byteOffset, byteLength and uncompressedByteLength. A cube's level holds its
 * faces one after the other: +X, −X, +Y, −Y, +Z, −Z (WebGL's order).
 */

/** «KTX 20»\r\n\x1A\n */
// prettier-ignore
export const KTX2_IDENTIFIER: Uint8Array = Uint8Array.of(
  0xab, 0x4b, 0x54, 0x58, 0x20, 0x32, 0x30, 0xbb, 0x0d, 0x0a, 0x1a, 0x0a,
);

/** VK_FORMAT_ASTC_6x6_SRGB_BLOCK: 16 bytes for each 6 × 6 block. */
export const VK_FORMAT_ASTC_6x6_SRGB_BLOCK = 166;

/** VK_FORMAT_ASTC_6x6_UNORM_BLOCK, the same blocks without the sRGB transfer. */
const VK_FORMAT_ASTC_6x6_UNORM_BLOCK = 165;
/** VK_FORMAT_R8G8B8A8_UNORM and _SRGB: 4 bytes a texel. */
const VK_FORMAT_R8G8B8A8_UNORM = 37;
const VK_FORMAT_R8G8B8A8_SRGB = 43;

/** How many `block`-px ASTC blocks cover `size` px (the last one partly). */
export function astcBlocks(size: number, block: number): number {
  return Math.ceil(size / block);
}

/** What the reader found. `faces` are views into the bytes it was given, not copies. */
export type Ktx2Cube = {
  vkFormat: number;
  width: number;
  height: number;
  faceCount: number;
  levelCount: number;
  supercompressionScheme: number;
  faces: Uint8Array[];
};

/** Why a file is not a cube we can show, in a short English reason. */
export class Ktx2Error extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Ktx2Error';
  }
}

const HEADER_BYTES = 80;
const LEVEL_INDEX_BYTES = 24;

/**
 * One face's bytes for the formats whose size we know, `undefined` for any
 * other (the reader then only asks that the level splits into six).
 */
function faceBytes(vkFormat: number, size: number): number | undefined {
  switch (vkFormat) {
    case VK_FORMAT_ASTC_6x6_SRGB_BLOCK:
    case VK_FORMAT_ASTC_6x6_UNORM_BLOCK:
      return astcBlocks(size, 6) ** 2 * 16;
    case VK_FORMAT_R8G8B8A8_UNORM:
    case VK_FORMAT_R8G8B8A8_SRGB:
      return size * size * 4;
    default:
      return undefined;
  }
}

/** A u64 as a number, or Infinity past 2^53 (which no file of ours reaches). */
function u64(view: DataView, at: number): number {
  const value = view.getBigUint64(at, true);
  return value > BigInt(Number.MAX_SAFE_INTEGER) ? Infinity : Number(value);
}

/**
 * The six faces of a single-level KTX2 cube map. Any `vkFormat` is read
 * (callers check theirs); throws `Ktx2Error` for anything else.
 */
export function readKtx2Cube(bytes: Uint8Array): Ktx2Cube {
  if (bytes.length < KTX2_IDENTIFIER.length) throw new Ktx2Error('short file');
  for (let i = 0; i < KTX2_IDENTIFIER.length; i++) {
    if (bytes[i] !== KTX2_IDENTIFIER[i]) throw new Ktx2Error('not a KTX2 file (identifier)');
  }
  if (bytes.length < HEADER_BYTES + LEVEL_INDEX_BYTES) throw new Ktx2Error('short file');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u32 = (at: number): number => view.getUint32(at, true);
  const vkFormat = u32(12);
  const width = u32(20);
  const height = u32(24);
  const pixelDepth = u32(28);
  const layerCount = u32(32);
  const faceCount = u32(36);
  const levelCount = u32(40);
  const supercompressionScheme = u32(44);

  if (faceCount !== 6) throw new Ktx2Error(`not a cube map (${faceCount} faces)`);
  // 0 would ask for mipmaps to be made at load, which no block-compressed format allows.
  if (levelCount !== 1) throw new Ktx2Error(`${levelCount} levels, expected one`);
  if (supercompressionScheme !== 0) {
    throw new Ktx2Error(`supercompressed (scheme ${supercompressionScheme})`);
  }
  if (pixelDepth !== 0 || layerCount !== 0) {
    throw new Ktx2Error('a 3D texture or an array (depth or layers)');
  }
  if (width === 0 || width !== height)
    throw new Ktx2Error(`faces not square (${width} × ${height})`);

  const offset = u64(view, HEADER_BYTES);
  const length = u64(view, HEADER_BYTES + 8);
  const face = faceBytes(vkFormat, width);
  const expected = face === undefined ? undefined : face * 6;
  if (expected !== undefined ? length !== expected : length === 0 || length % 6 !== 0) {
    throw new Ktx2Error(
      `level is ${length} bytes, expected ${expected ?? 'six equal faces'} for ${width} px`,
    );
  }
  if (offset < HEADER_BYTES + LEVEL_INDEX_BYTES || offset + length > bytes.length) {
    throw new Ktx2Error('level outside the file');
  }
  const each = length / 6;
  const faces: Uint8Array[] = [];
  for (let i = 0; i < 6; i++) {
    faces.push(bytes.subarray(offset + i * each, offset + (i + 1) * each));
  }
  return {
    vkFormat,
    width,
    height,
    faceCount,
    levelCount,
    supercompressionScheme,
    faces,
  };
}

/**
 * The basic data format descriptor of ASTC 6x6 sRGB (Khronos Data Format 1.3,
 * as `ktx create` writes it): one 128-bit sample, the whole block.
 */
function astc6x6SrgbDfd(): Uint8Array {
  const dfd = new Uint8Array(44);
  const view = new DataView(dfd.buffer);
  view.setUint32(0, 44, true); // dfdTotalSize
  view.setUint32(4, 0, true); // vendorId Khronos (0), descriptorType basic (0)
  view.setUint16(8, 2, true); // versionNumber: 1.3
  view.setUint16(10, 24 + 16, true); // descriptorBlockSize: one sample
  dfd.set([162, 1, 2, 0], 12); // KHR_DF_MODEL_ASTC, BT709 primaries, sRGB, straight alpha
  dfd.set([5, 5, 0, 0], 16); // texelBlockDimension 6 × 6 × 1 × 1, each less one
  dfd[20] = 16; // bytesPlane0: one block
  view.setUint16(28, 0, true); // sample bitOffset
  dfd[30] = 127; // bitLength, less one: 128 bits
  dfd[31] = 0; // channelType KHR_DF_CHANNEL_ASTC_DATA, no qualifiers
  view.setUint32(36, 0, true); // sampleLower
  view.setUint32(40, 0xffffffff, true); // sampleUpper
  return dfd;
}

/**
 * A single-level KTX2 cube map, for tests and fixtures only (the worker's real
 * files come from `ktx create`). For ASTC 6x6 sRGB the file is a valid KTX2
 * file: a basic data format descriptor, no key/value data (so no KTXwriter,
 * which `ktx validate` only warns about), the level aligned to 16 bytes. For
 * any other format the descriptor is empty (only its total size), so such a
 * file is for our own reader only.
 */
export function writeKtx2Cube(cube: {
  vkFormat: number;
  size: number;
  faces: readonly Uint8Array[];
}): Uint8Array {
  const { vkFormat, size, faces } = cube;
  if (faces.length !== 6) throw new Error(`a cube has six faces, not ${faces.length}`);
  const each = faces[0]!.length;
  if (faces.some((face) => face.length !== each)) throw new Error('faces of different lengths');
  if (!Number.isSafeInteger(size) || size <= 0) throw new Error(`no face side ${size}`);
  const want = faceBytes(vkFormat, size);
  if (want !== undefined && each !== want) {
    throw new Error(`a ${size} px face is ${want} bytes in format ${vkFormat}, not ${each}`);
  }

  const dfd =
    vkFormat === VK_FORMAT_ASTC_6x6_SRGB_BLOCK ? astc6x6SrgbDfd() : Uint8Array.of(4, 0, 0, 0);
  const dfdOffset = HEADER_BYTES + LEVEL_INDEX_BYTES;
  // The level is aligned to lcm(texel block size, 4): 16 covers every format here.
  const levelOffset = Math.ceil((dfdOffset + dfd.length) / 16) * 16;
  const levelLength = each * 6;
  const bytes = new Uint8Array(levelOffset + levelLength);
  const view = new DataView(bytes.buffer);
  bytes.set(KTX2_IDENTIFIER, 0);
  const header = [vkFormat, 1, size, size, 0, 0, 6, 1, 0];
  header.forEach((value, i) => view.setUint32(12 + i * 4, value, true));
  view.setUint32(48, dfdOffset, true);
  view.setUint32(52, dfd.length, true);
  // kvd (56, 60) and sgd (64, 72) stay 0: none.
  view.setBigUint64(HEADER_BYTES, BigInt(levelOffset), true);
  view.setBigUint64(HEADER_BYTES + 8, BigInt(levelLength), true);
  view.setBigUint64(HEADER_BYTES + 16, BigInt(levelLength), true);
  bytes.set(dfd, dfdOffset);
  faces.forEach((face, i) => bytes.set(face, levelOffset + i * each));
  return bytes;
}
